import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FaComments } from "react-icons/fa";
import { IoIosSend } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { BASELINE } from "../../../util";

// 오답/정답 데이터에서 취약 토픽 상위 N개 추출 (DigitalSatView와 동일 로직)
function findTopMissedTopics(incorrectData = [], correctData = [], tag, limit = 5) {
    const incorrectCount = {};
    const totalCount = {};

    incorrectData.forEach(item => {
        if (item.tag === tag) {
            incorrectCount[item.topic] = (incorrectCount[item.topic] || 0) + 1;
            totalCount[item.topic] = (totalCount[item.topic] || 0) + 1;
        }
    });

    correctData.forEach(item => {
        if (item.tag === tag) {
            totalCount[item.topic] = (totalCount[item.topic] || 0) + 1;
        }
    });

    return Object.entries(incorrectCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([topic, count]) => ({ topic, count, total: totalCount[topic] || count }));
}

// **bold** 만 허용하는 최소 렌더러 (HTML escape 후 변환)
function renderMessageHtml(text) {
    const escaped = String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    return escaped
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br/>");
}

const QUICK_PROMPTS = [
    "What should I study next?",
    "Analyze my study pattern",
    "Give me a study tip",
];

const StudyCoachChat = ({
    user,
    avg_sat = 0,
    avg_eng = 0,
    avg_math = 0,
    allSATScores = [],
    Incorrect_questionIds = [],
    Correct_questionIds = [],
}) => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading, open]);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open]);

    // 첫 오픈 시 인사말 (API 호출 없이 로컬 생성)
    useEffect(() => {
        if (open && messages.length === 0) {
            setMessages([{
                role: "assistant",
                content: `Hi ${user?.first_name || "there"}! 👋 I'm your SAT Study Coach. Ask me what to study next, or about your study pattern and methods.`,
            }]);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const buildStats = () => ({
        avg_sat,
        avg_eng,
        avg_math,
        recentScores: allSATScores.slice(-6),
        weakEng: findTopMissedTopics(Incorrect_questionIds, Correct_questionIds, "eng"),
        weakMath: findTopMissedTopics(Incorrect_questionIds, Correct_questionIds, "math"),
    });

    const sendMessage = async (text) => {
        const trimmed = (text ?? input).trim();
        if (!trimmed || loading) return;

        const userMessage = { role: "user", content: trimmed };
        const nextMessages = [...messages, userMessage];
        setMessages(nextMessages);
        setInput("");
        setLoading(true);

        try {
            const { data } = await axios.post(`${BASELINE}studycoach/chat`, {
                message: trimmed,
                // 첫 인사말 제외한 최근 대화만 전송
                history: nextMessages.slice(1, -1).slice(-12),
                stats: buildStats(),
                name: user?.first_name || "student",
            });
            setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
        } catch (err) {
            console.error("Study coach chat error:", err);
            setMessages(prev => [...prev, {
                role: "assistant",
                content: "Sorry, something went wrong. Please try again in a moment.",
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <>
            {/* Floating chat icon */}
            <motion.button
                onClick={() => setOpen(prev => !prev)}
                className="fixed bottom-6 right-6 z-[60] flex items-center justify-center w-14 h-14 rounded-full text-white shadow-xl"
                style={{ background: "linear-gradient(135deg, #3b82f6, #8b5cf6)" }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                aria-label="Open study coach chat"
            >
                {open ? <IoClose size={26} /> : <FaComments size={22} />}
            </motion.button>

            {/* Chat panel */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.96 }}
                        transition={{ duration: 0.2 }}
                        className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-[370px] h-[480px] max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
                    >
                        {/* Header */}
                        <div
                            className="flex items-center gap-3 px-4 py-3"
                            style={{ background: "linear-gradient(135deg, #1e3a8a, #3b82f6)" }}
                        >
                            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white/15 text-white">
                                ✨
                            </div>
                            <div className="flex-1">
                                <p className="text-white text-sm font-bold leading-tight">SAT Study Coach</p>
                                <p className="text-blue-200 text-[11px]">Topic picks · Study patterns · Tips</p>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="text-blue-200 hover:text-white transition"
                                aria-label="Close chat"
                            >
                                <IoClose size={20} />
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50">
                            {messages.map((msg, i) => (
                                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                                    <div
                                        className={`max-w-[80%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed ${
                                            msg.role === "user"
                                                ? "bg-blue-600 text-white rounded-br-sm"
                                                : "bg-white text-slate-700 border border-slate-200 rounded-bl-sm shadow-sm"
                                        }`}
                                        dangerouslySetInnerHTML={{ __html: renderMessageHtml(msg.content) }}
                                    />
                                </div>
                            ))}

                            {/* Quick prompts (only right after greeting) */}
                            {messages.length === 1 && !loading && (
                                <div className="flex flex-wrap gap-1.5 pt-1">
                                    {QUICK_PROMPTS.map((q) => (
                                        <button
                                            key={q}
                                            onClick={() => sendMessage(q)}
                                            className="text-[12px] px-3 py-1.5 rounded-full border border-blue-200 bg-white text-blue-600 hover:bg-blue-50 transition"
                                        >
                                            {q}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {loading && (
                                <div className="flex justify-start">
                                    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex gap-1">
                                        {[0, 1, 2].map(i => (
                                            <span
                                                key={i}
                                                className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
                                                style={{ animationDelay: `${i * 0.15}s` }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input bar */}
                        <div className="flex items-center gap-2 px-3 py-2.5 border-t border-slate-200 bg-white">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Ask about what to study..."
                                className="flex-1 text-[13px] px-3 py-2 rounded-xl bg-slate-100 border border-transparent focus:bg-white focus:border-blue-400 focus:outline-none transition"
                            />
                            <button
                                onClick={() => sendMessage()}
                                disabled={loading || !input.trim()}
                                className="flex items-center justify-center w-9 h-9 rounded-xl bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                aria-label="Send message"
                            >
                                <IoIosSend size={18} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default StudyCoachChat;
